import { EmailAddressesTypeForTable } from 'components/Auth/types';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { Dispatch, SetStateAction } from 'react';
import { AppDispatch } from 'store';
import {
    changeSplashScreen,
    setDefaultFilters,
    setUserDefaultFilters,
    setUserSettings,
} from 'store/auth/authSlice';
import {
    ADDITIONAL_SETTINGS_INSTRUCTION,
    AUTH_WHITELIST,
    DEFAULT_FILTERS,
    USER_DEFAULT_FILTERS,
    USER_SETTINGS,
} from 'utils/config/constants/controllerNames';
import { auth, db } from 'utils/config/firebase';
import { errorLogger } from 'utils/config/firebase/logs';

export const getUserSettingsQuery = (dispatch: AppDispatch) => {
    try {
        const collectionRef = collection(db, USER_SETTINGS);
        const q = query(collectionRef, where('user_id', '==', auth?.currentUser?.uid));
        onSnapshot(q, (snapshot) => {
            const filteredData = snapshot.docs.map((doc) => ({
                user_type: doc.data().user_type,
                brand: doc.data().brand,
                target_country: doc.data().target_country,
                target_country_code: doc.data().target_country_code,
                output_language_title: doc.data().output_language_title,
                output_language_code: doc.data().output_language_code,
                document_id: doc.id,
                show_full_product_tour: doc.data().show_full_product_tour,
                dont_show_general_generate: doc.data().dont_show_general_generate,
                dont_show_additional_settings_example:
                    doc.data()?.dont_show_additional_settings_example ?? false,
            }));
            if (filteredData?.length !== 0) {
                dispatch(setUserSettings(filteredData[0]));
            }
            dispatch(changeSplashScreen(false));
        });
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getUserSettingsQuery');
        }
        console.log(error);
    }
};

export const getDefaultFiltersQuery = (dispatch: AppDispatch) => {
    try {
        const collectionRef = collection(db, DEFAULT_FILTERS);
        const q = query(collectionRef);
        onSnapshot(q, (snapshot) => {
            const filteredData = snapshot.docs.map((doc) => ({
                items: doc.data().items,
                document_id: doc.id,
            }));
            if (filteredData?.length !== 0) {
                dispatch(setDefaultFilters(filteredData[0]));
            }
        });
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getDefaultFiltersQuery');
        }
        console.log(error);
    }
};

export const getUserDefaultFiltersQuery = (dispatch: AppDispatch) => {
    try {
        const collectionRef = collection(db, USER_DEFAULT_FILTERS);
        const q = query(collectionRef, where('user_id', '==', auth?.currentUser?.uid));
        onSnapshot(q, (snapshot) => {
            const filteredData = snapshot.docs.map((doc) => {
                let tone_of_voice = doc.data().tone_of_voice;
                if (doc.data().toneOfVoice && !tone_of_voice) {
                    tone_of_voice = doc.data().toneOfVoice;
                }
                return {
                    anonymized: doc.data().anonymized,
                    formality: doc.data().formality,
                    length: doc.data().length,
                    structures: doc.data().structures,
                    tone_of_voice,
                    urgency: doc.data().urgency,
                    document_id: doc.id,
                };
            });
            if (filteredData?.length !== 0) {
                dispatch(setUserDefaultFilters(filteredData[0]));
            }
        });
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getUserDefaultFiltersQuery');
        }
        console.log(error);
    }
};

export const getInstructionQuery = (dispatch: AppDispatch, doc_id?: string) => {
    try {
        if (!doc_id) {
            return null;
        }
        const unsubscribe = onSnapshot(doc(db, ADDITIONAL_SETTINGS_INSTRUCTION, doc_id), (doc) => {
            const data = doc.data();
            if (data?.value) {
                dispatch(setUserDefaultFilters({ instructions: data?.value }));
            }
        });
        return unsubscribe;
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getUserDefaultFiltersQuery');
        }
        console.log(error);
        return null;
    }
};

export const getWhitelistedUsers = (
    setUsers: Dispatch<SetStateAction<EmailAddressesTypeForTable | null>>,
) => {
    try {
        const collectionRef = collection(db, AUTH_WHITELIST);
        const q = query(collectionRef);
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (snapshot.docs[0]) {
                const users = snapshot?.docs[0].data()?.emailAddresses;
                const data = {
                    emailAddresses: snapshot?.docs[0]
                        .data()
                        ?.emailAddresses?.map((item: string) => ({
                            email: item,
                            domain: item?.split('.')?.at(-1),
                        })),
                    document_id: snapshot?.docs[0].id,
                };

                if (users) {
                    setUsers(data);
                }
            }
        });
        return unsubscribe;
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getWhitelistedUsers');
        }
        console.log(error);
    }
};
